import { useEffect, useRef, useState } from "react";
import { ImageType } from "@/pages/challenges/types/common";
import { getImageByPriority } from "@/shared/utils/common";
import { cn } from "@/shared/utils";

type ImageSize = "tiny" | "small" | "medium" | "large";

interface ImageProps {
  urls: ImageType;
  size: ImageSize;
  fallback?: string;
  alt: string;
  className?: string;
  loading?: "eager" | "lazy";
  wrapperClassName?: string;
  imgClassName?: string;
  debugTransition?: boolean;
}

const Image = ({
  urls: propsUrls,
  size,
  alt,
  className,
  loading = "lazy",
  fallback,
  debugTransition = false,
  wrapperClassName,
  imgClassName,
}: ImageProps) => {
  const [loaded, setLoaded] = useState(false);
  const [, setShowDebug] = useState(debugTransition);

  const mounted = useRef(false);
  const urls: Record<ImageSize, string> = {
    tiny: propsUrls?.tiny_url,
    small: propsUrls?.small_url,
    medium: propsUrls?.medium_url,
    large: propsUrls?.large_url,
  };

  // Debug mode simulation
  useEffect(() => {
    mounted.current = true;
    if (debugTransition) {
      const timer = setTimeout(() => setShowDebug(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [debugTransition]);

  return (
    <picture className={cn("size-full relative overflow-hidden", className)}>
      <source type="image/webp" srcSet={generateSrcSet()} />

      <div
        className={cn("relative size-full overflow-hidden ", wrapperClassName)}
      >
        <div
          className="absolute inset-0 z-0 transition-opacity duration-500"
          style={{
            background: `url(${urls.tiny}) no-repeat center/cover`,
            opacity: loaded ? 0 : 1,
            filter: "brightness(0.6)",
          }}
        />

        <img
          src={urls[size] ?? (getImageByPriority(propsUrls, size) || fallback)}
          srcSet={generateSrcSet()}
          alt={alt}
          loading={loading}
          decoding="async"
          onLoad={() => {
            if (debugTransition) {
              setTimeout(() => {
                setLoaded(true);
              }, 1000);
            } else {
              setLoaded(true);
            }
          }}
          className={cn(
            "absolute inset-0 z-10 h-full  w-full object-cover transition-all duration-300",
            imgClassName
          )}
          style={{
            filter: loaded ? "brightness(1)" : "brightness(0.8)",
            opacity: loaded ? 1 : 0,
          }}
        />
      </div>
    </picture>
  );

  function generateSrcSet() {
    const sizeHierarchy: ImageSize[] = ["tiny", "small", "medium", "large"];
    const currentSizeIndex = sizeHierarchy.indexOf(size);

    return (
      sizeHierarchy
        .slice(currentSizeIndex)
        .filter((e) => {
          return Boolean(urls[e]);
        })
        .map((s, i) => `${urls[s] ?? propsUrls.url} ${i + 1}x`)
        .join(", ") ?? ""
    );
  }
};

export default Image;
